// Modules
import videojs from 'video.js';
// Helpers
import { runAtTime, isTransparent, getALLinkedVariantId, getLinkedVariantId, isVariantInHistory } from './helpers';
import { getChoicesToDisplay } from './helpers/variants';
import { toNumber } from 'lodash';
import { substituteShortcodes } from './helpers/graphicOverlay';

// Events
export const EVENT_WILL_SELECT_CHOICE = 'player:will-select-choice';
export const EVENT_UNLINKED_CHOICE = 'player:unlinked-choice';
export const EVENT_DID_SELECT_CHOICE = 'player:did-choice';
const MAX_TIMER_WIDTH = 100;

export class ChoicesBar extends videojs.getComponent('Component') {
  constructor(player, options = {}) {
    super(player, options);
    this.aLContext = options.aLContext;

    this.player = player;
    this.options = options;

    Object.assign(this, options);

    // Aids positioning elements in the player.
    this.createContainer();
    this.createTimer();
    this.update(this.variant);
  }

  // Method to show a specific button by removing the 'invisible' class
  showChoiceButton(choiceId) {
    const button = this.container.querySelector(`[data-id='${choiceId}']`);
    if (button) {
      button.classList.remove('invisible');
    }
  }

  // Method to hide a specific button by adding the 'invisible' class
  hideChoiceButton(choiceId) {
    const button = this.container.querySelector(`[data-id='${choiceId}']`);
    if (button) {
      button.classList.add('invisible');
    }
  }

  handleShowHideTimes(choice) {
    const showHideTimes = choice.show_hide_at;

    // Iterate over each key in the showHideTimes object
    Object.keys(showHideTimes).forEach((index) => {
      const { show, hide } = showHideTimes[index];

      // Handle the show time
      if (!isNaN(show)) {
        if (!this.timeGroups[show]) {
          this.timeGroups[show] = { buttonsToShow: [], buttonsToHide: [] };
        }
        this.timeGroups[show].buttonsToShow.push(choice.id);
      }

      // Handle the hide time
      if (!isNaN(hide)) {
        if (!this.timeGroups[hide]) {
          this.timeGroups[hide] = { buttonsToShow: [], buttonsToHide: [] };
        }
        this.timeGroups[hide].buttonsToHide.push(choice.id);
      }
    });
  }

  initializeChoiceVisibilityHandling(choices) {
    this.timeGroups = {}; // Initialize the time groups
    choices.forEach((choice) => {
      if (choice.show_hide_at) {
        this.handleShowHideTimes(choice);
      }
    });

    // Setup the runAtTime for each time group
    Object.keys(this.timeGroups).forEach((time) => {
      const { buttonsToShow, buttonsToHide } = this.timeGroups[time];
      this.runAtTime({
        player: this.player,
        displayAt: parseFloat(time),
        buttonsToShow: buttonsToShow,
        buttonsToHide: buttonsToHide,
        runOnce: true,
      });
    });
  }

  runAtTime({ player, displayAt, buttonsToShow, buttonsToHide, runOnce }) {
    const checkTime = () => {
      const currentTime = player.currentTime();
      if (currentTime >= displayAt) {
        // Batch show/hide operations
        if (buttonsToShow) {
          buttonsToShow.forEach((choiceId) => this.showChoiceButton(choiceId));
        }
        if (buttonsToHide) {
          buttonsToHide.forEach((choiceId) => this.hideChoiceButton(choiceId));
        }

        if (runOnce) {
          player.off('timeupdate', checkTime);
        }
      }
    };

    player.on('timeupdate', checkTime);
  }

  createEl() {
    return videojs.dom.createEl('div', {
      className: 'vjs-wrp-choices-bar vjs-remain-visible-when-inactive',
    });
  }

  // Used to populate ChoicesBar at render and when updating.
  // Use when navigating choices.
  update(variant) {
    // Hides timer as a first thing
    // so it's hidden regardless of whether there's a variant
    this.hideTimer();

    if (variant) {
      const choices = getChoicesToDisplay(variant, this.history);

      this.selectedChoice = null;
      this.unlinkedChoice = null;
      this.setupStyles(variant, choices);

      if (variant.choices) {
        this.updateChoices(choices, variant, this.history, this.aLContext);
      }

      // Initialize visibility handling for choices
      this.initializeChoiceVisibilityHandling(choices);

      if (variant.choices && variant.prompt.display_at) {
        runAtTime({
          player: this.player,
          displayAt: variant.prompt.display_at,
          callback: () => {
            this.showEndButtons();
          },
          elseCallback: () => {
            this.hideEndButtons();
          },
          runOnce: false,
        });
      }

      if (variant.prompt.timer && variant.prompt.timer.show && choices.length) {
        this.updateTimerColors(variant);

        // Remove transitions the time of the reset so the timer
        // doesn't animate back to start when player is reused
        this.timerContainer.style.transition = 'none';
        requestAnimationFrame(() => {
          this.timerContainer.style.transition = null;
        });
        // Reset timer size so there's no risk of it starting not full width
        // because of rounding issues.
        this.timerContainer.style.backgroundSize = `100% 100%`;
        // Start with timer hidden, though that should be the responsibility
        // of the Choice bar itself
        this.hideTimer();

        runAtTime({
          player: this.player,
          displayAt: variant.prompt.display_at,
          callback: (currentTime, duration) => {
            // For solid timer bar using text and bg colours
            let timeLeft = Math.ceil(duration - currentTime);

            if (timeLeft == 0) {
              this.hideTimer();
            } else {
              this.showTimer();

              let timerDuration = Math.min(toNumber(variant.prompt.display_at), duration) - 1;

              let elapsedTimerTime = toNumber(timeLeft) - 2;

              // if (elapsedTimerTime >= 0) {
              //   elapsedTimerTime = elapsedTimerTime - 2;
              // }
              let step = MAX_TIMER_WIDTH / timerDuration;

              let timerBarWidth = elapsedTimerTime * step;

              if (timerBarWidth >= MAX_TIMER_WIDTH) {
                timerBarWidth = MAX_TIMER_WIDTH;
              }

              this.timerContainer.style.backgroundSize = `${timerBarWidth}% 100%`;
            }

            /* For use IF asked for a transparent center with two bars closing in
                Change MAX_TIMER_WIDTH to 50
                  if (timeLeft == 0) {
                    this.timerContainer.style.display = 'block';
                    for (let i = 0; i < this.timers.length; ++i) {
                      //TODO: Swap for classes
                      this.timers[i].style.transitionDuration = '0s';
                      this.timers[i].style.opacity = '0';
                      setTimeout(() => {
                        this.timers[i].style.width = '0';
                      }, 500);
                    }
                  } else {
                    for (let i = 0; i < this.timers.length; ++i) {
                      //TODO: Swap for classes
                      this.timerContainer.style.display = 'block';
                      this.timers[i].style.transitionDuration = '1s';
                      this.timers[i].style.opacity = '1';

                      // Bar Colours
                      if (variant.prompt.timer.invert_colors) {
                        this.timers[i].style.backgroundColor =
                          variant.prompt.text_color;
                      } else {
                        this.timers[i].style.backgroundColor =
                          variant.prompt.background_color;
                      }

                      let timerDuration = toNumber(variant.prompt.display_at) - 1;

                      let elapsedTimerTime = toNumber(
                        variant.prompt.display_at - timeLeft
                      );

                      // if (elapsedTimerTime >= 0) {
                        elapsedTimerTime = elapsedTimerTime + 1;
                      // }
                      let step = MAX_TIMER_WIDTH / timerDuration;

                      let timerBarWidth = elapsedTimerTime * step;

                      if (timerBarWidth >= MAX_TIMER_WIDTH) {
                        timerBarWidth = MAX_TIMER_WIDTH;
                      }

                      // this.timers[i].style.width = timerBarWidth + '%';
                      this.timers[i].style.backgroundSize = `100% ${timerBarWidth}%`;
                    }
                  }
                } else {
                  this.timerContainer.style.display = 'none';
              } */
          },
          elseCallback: () => {
            this.hideTimer();
          },
          runOnce: false,
        });
      } else {
        this.hideTimer();
      }
      // }

      if (variant.prompt.style === 'custom_button_positions') {
        this.applyCustomButtonStyles(choices);
        this.container.classList.add('vjs-wrp-choices-bar__custom-button-positions-container');
        this.el().classList.add('vjs-wrp-choices-bar--custom-button-positions');
      } else {
        this.container.classList.remove('vjs-wrp-choices-bar__custom-button-positions-container');
        this.el().classList.remove('vjs-wrp-choices-bar--custom-button-positions');
      }
    }
  }

  applyCustomButtonStyles(choices) {
    const buttons = this.container.querySelectorAll('.js-choice-button');

    buttons.forEach((button, index) => {
      // Assuming the choices array has the same order as the buttons
      const choice = choices[index];
      if (choice) {
        const { width, height, position_x, position_y } = choice; // Extract custom positions and dimensions

        button.style.position = 'absolute';
        button.style.width = (width || '15') + '%';
        button.style.height = height + '%';
        button.style.left = position_x + '%';
        button.style.top = position_y + '%';
        // button.style.transform = 'translate(-50%, -50%)'; add this if we want to center the button, currently anchored to top left

        // Add classes to the images within each button
        const baseImage = button.querySelector('.js-image-base');
        const hoverImage = button.querySelector('.js-image-hover');
        const activeImage = button.querySelector('.js-image-active');
        if (baseImage) {
          baseImage.classList.add('js-image-base--custom-button-positions');
        }
        if (hoverImage) {
          hoverImage.classList.add('js-image-hover--custom-button-positions');
        }
        if (activeImage) {
          activeImage.classList.add('js-image-active--custom-button-positions');
        }
      }
    });
  }
  // Removes all choices and related DOM elements.
  // Useful when an end variant is reached and no choices are provided.
  empty() {
    videojs.dom.emptyEl(this.container);
  }

  // Styles set in the project settings for a variant.
  setupStyles(variant, choices) {
    const { background_color, text_color, style, substyle } = variant.prompt;

    let styles = ``;

    if (background_color) {
      styles += `background: ${background_color};`;
    }

    if (text_color) {
      styles += `color: ${text_color};`;
    }

    if (!isTransparent(text_color)) {
      // Adds a slight shadow to the text to aid visibilty when colours with low contrast ratio are picked.
      const outlineColor = 'rgba(255,255,255, 0)';
      styles += `text-shadow: 0.5px 1px ${outlineColor};`;
    }

    // Set a data-attribute to hook some CSS selectors
    this.setAttribute('data-number-of-choices', choices.length);
    // But also a custom property to use in grid setup without cluttering the CSS
    this.el().style.setProperty('--number-of-choices', choices.length);

    if (style) {
      this.setAttribute('class', 'vjs-wrp-choices-bar');
      this.setAttribute('data-substyle', substyle || '');
      if (substyle) {
        switch (substyle) {
          case 'central_text':
            this.addClass('text_split_quadrant');
            break;
          case 'bottom_button':
            this.addClass('button_split_quadrant');
            break;
          case 'bottom_banner':
            this.addClass('banner_split_quadrant');
            break;
          default:
            break;
        }
      }
      this.addClass(`${style}`);

      //Check if variant uses banner layout, if so add class and cause timer to sit above it
      if (style === 'bottom_banner') {
        this.addClass('bottom_banner--tall_banner_buttons');
      }
    }

    // Remove margins on container if layout is of new design, or hotspot selected
    let layout = ['split_quadrant', 'split_top_bottom', 'split_left_right', 'custom_button_positions'];
    if (layout.includes(variant.prompt.style) || variant.prompt.hotspot) {
      this.addClass('js-no-mg');
    }

    if (isTransparent(background_color)) {
      this.addClass(`transparent`);
    }

    this.hideTimer();
    this.styles = styles;
  }

  // Method to show all buttons with the class 'wrp-display-at-end' by removing the 'invisible' class
  showEndButtons() {
    const buttons = this.container.querySelectorAll('.wrp-display-at-end'); // Select all elements with the class
    buttons.forEach((button) => {
      button.classList.remove('invisible'); // Remove the 'invisible' class from each button
    });
  }

  // Method to hide all buttons with the class 'wrp-display-at-end' by adding the 'invisible' class
  hideEndButtons() {
    const buttons = this.container.querySelectorAll('.wrp-display-at-end'); // Select all elements with the class
    buttons.forEach((button) => {
      button.classList.add('invisible'); // Add the 'invisible' class to each button
    });
  }

  showAllChoicesForNonTimedVariants() {
    const buttons = this.container.querySelectorAll('.js-choice-button');
    const prompt = this.container.querySelector('.vjs-wrp-choices-bar-prompt');
    buttons.forEach((button) => button.classList.remove('invisible'));
    if (prompt) {
      prompt.classList.remove('invisible');
    }
  }

  hideTimer() {
    this.timerContainer.style.opacity = '0';
    // Timer is not visible, so shouldn't be in the accessibility tree
    this.timerContainer.style.visibility = 'hidden';
  }

  showTimer() {
    this.timerContainer.style.opacity = '1';
    this.timerContainer.style.visibility = 'visible';
  }

  updateTimerColors(variant) {
    // Bar Colours
    if (variant.prompt.timer.invert_colors) {
      this.timerContainer.style.backgroundColor = variant.prompt.text_color;

      this.timerContainer.style.backgroundImage = `linear-gradient(${variant.prompt.background_color},${variant.prompt.background_color})`;
    } else {
      this.timerContainer.style.backgroundColor = variant.prompt.background_color;
      this.timerContainer.style.backgroundImage = `linear-gradient(${variant.prompt.text_color},${variant.prompt.text_color})`;
    }

    if (variant.prompt.timer.transparent_background) {
      this.timerContainer.style.backgroundColor = 'transparent';
    }
  }

  createContainer() {
    let className = 'vjs-wrp-choices-bar__container';

    let container = videojs.dom.createEl('div', {
      className: className,
    });

    this.el().appendChild(container);

    this.container = container;
  }

  createTimer() {
    let timerContainer = videojs.dom.createEl('div', {
      // If transparent needs class 'wrp-timer-transparent' instead
      className: 'wrp-timer-colors',
    });

    videojs.dom.appendContent(this.contentEl(), timerContainer);
    this.timerContainer = timerContainer;

    // If timer to have transparent middle
    // let timerLeft = videojs.dom.createEl('div', {
    //   className: 'wrp-timer-left '
    // });
    // let timerRight = videojs.dom.createEl('div', {
    //   className: 'wrp-timer-right '
    // });

    // videojs.dom.appendContent(timerContainer, timerLeft);
    // videojs.dom.appendContent(timerContainer, timerRight);
    // this.timers = [timerLeft, timerRight];
  }

  updatePrompt(variant) {
    const { text, hidden } = variant.prompt;
    const promptText = substituteShortcodes(text, this.aLContext);

    if (!hidden && text) {
      const promptEl = videojs.dom.createEl('p', {
        className: 'vjs-wrp-choices-bar-prompt vjs-remain-visible-when-inactive invisible wrp-display-at-end',
        innerHTML: promptText,
        style: this.styles ? this.styles : null,
      });

      this.container.insertAdjacentElement('afterbegin', promptEl);
    }
  }

  updateChoices(choices, variant, history, aLContext) {
    this.empty();
    if (variant.prompt) {
      this.updatePrompt(variant);
    }

    // Expose player to window object when in development mode.
    if (process.env.NODE_ENV == 'development') {
      window.choices = variant.choices;
    }

    const filteredChoices = choices;

    // Uses choices data to turn it them into videoJS elements:
    let usedLocations = [];
    const choicesElements = filteredChoices.reduce((result, choice) => {
      //Check if has location
      let locationClasses = '';

      if (['split_left_right', 'split_top_bottom', 'split_quadrant'].includes(variant.prompt.style)) {
        locationClasses += 'vjs-wrp-button--absolute ';

        if (choice.location && !usedLocations.includes(choice.location)) {
          locationClasses += choice.location;
          usedLocations.push(choice.location);
        } else {
          // If these absolutely positioned styles do not have a location defined, or have a location that is already set,
          // then the chocie is not displayed at all. So we return early without creating a formattedChoice el.
          return result;
        }
      }

      let isHotspot = '';
      let buttonText = '';
      let buttonTextToProcess = '';
      let isImage = '';
      let buttonWidth = '';
      let buttonTextAdjust = '';
      let totalStyles = '';
      let isUnlinked = '';
      let displayAtEnd = '';
      let showHideAt = '';

      // If bottom banner with images theres a requirement to keep spacing which is lost
      // due to absolutely positioned images
      if (variant.prompt.style == 'bottom_banner') {
        buttonWidth = 'min-width:' + 100 / variant.choices.length + '%';
        //Add padding to keep text in line with standard buttons
        if (choice.image_url !== 'undefined' && choice.image_url !== null) {
          buttonTextAdjust = 'padding-bottom: 15%;';
        }
      }

      //Check Hotspot -> therefore image or text doesn't matter at all
      if (variant.prompt.hotspot) {
        isHotspot = 'js-choice-hotspot';

        if (choice.show_hide_at) {
          showHideAt = 'wrp-show-hide-at';
        } else {
          displayAtEnd = 'wrp-display-at-end';
        }
      } else {
        //Set button text
        buttonTextToProcess = choice.label;
        buttonText = substituteShortcodes(buttonTextToProcess, this.aLContext);

        //Check if has image and add class
        if (
          (typeof choice.image_url !== 'undefined' && choice.image_url !== null) ||
          (typeof choice.image_hover_url !== 'undefined' && choice.image_hover_url !== null) ||
          (typeof choice.image_active_url !== 'undefined' && choice.image_active_url !== null)
        ) {
          if (variant.prompt.style === 'custom_button_positions') {
            isImage = 'js-choice-img js-choice-img--custom-button-positions vjs-wrp-button--img';
          } else {
            isImage = 'js-choice-img vjs-wrp-button--img';
          }
        }

        //Create Styles -> as they matter now
        //This code relates to that in resetChoicesState( function in video.js
        if (this.styles) {
          //Get user styles from styles string
          let styles = this.styles.split(';');
          let bgColor = styles[0];
          let color = styles[1];
          let textShadow = styles[2];

          // if (isImage) {
          //   let imageUrl = `url(${choice.image_url})`;
          //
          //   if (choice.image_active_url) {
          //     imageUrl += `, url(${choice.image_active_url})`;
          //   }
          //
          //   bgColor = `background-color:transparent;background-image:${imageUrl};background-position:center,-999999px;background-size:contain;background-repeat:no-repeat;`;
          // }
          totalStyles = color + '; ' + textShadow + '; ' + bgColor + '; ' + buttonWidth;
        }

        if (choice.show_hide_at) {
          showHideAt = 'wrp-show-hide-at';
        } else {
          displayAtEnd = 'wrp-display-at-end';
        }
      }
      const link = choice.link;
      const isLink = link?.external_url;
      const attributes = isLink
        ? {
            href: link.external_url,
          }
        : { type: 'button' };

      if (isLink && link.external_url_target) {
        attributes['target'] = link.external_url_target;
        attributes['rel'] = 'noreferrer noopener';
      }

      if (!choice.link) {
        isUnlinked = 'js-choice-unlinked'; // If no link, create class
      }

      const el = videojs.dom.createEl(
        isLink ? 'a' : 'button',
        {
          className:
            'btn btn-dark focus-visible vjs-wrp-button js-select-variant js-choice-button invisible ' +
            isUnlinked + // If no link, add class to handle click behaviour
            ' ' +
            isHotspot +
            ' ' +
            locationClasses +
            ' ' +
            isImage +
            ' ' +
            showHideAt +
            ' ' +
            displayAtEnd,
          style: this.styles ? totalStyles : null,
        },
        {
          'data-id': choice.id,
          'data-variant-id': variant.id,
          'data-island-id': variant.island_id,
          'data-cut-on-click': choice.cut_on_click,
          'data-go-to-time': choice.go_to_time || '0',
          'aria-label': buttonText,
          ...attributes,
        },
      );

      if (isImage) {
        if (choice.image_url) {
          const img = videojs.dom.createEl(
            'img',
            {},
            {
              src: choice.image_url,
              class: 'js-image-base' + (choice.image_hover_url ? ' has-hover' : ' no-hover'),
            },
          );

          videojs.dom.appendContent(el, img);
        }

        if (choice.image_active_url) {
          const imgActive = videojs.dom.createEl(
            'img',
            {},
            {
              src: choice.image_active_url,
              class: 'js-image-active js-image-hide' + (choice.image_hover_url ? ' has-hover' : ' no-hover'),
            },
          );

          videojs.dom.appendContent(el, imgActive);
        }

        if (choice.image_hover_url) {
          const imgHover = videojs.dom.createEl(
            'img',
            {},
            {
              src: choice.image_hover_url,
              class: 'js-image-hover',
            },
          );

          videojs.dom.appendContent(el, imgHover);
        }
      }

      const textSpanClasses = 'vjs-wrp-button__text' + (choice.show_button_text === false && isImage ? ' d-none' : '');

      const textSpan = videojs.dom.createEl(
        'span',
        {
          className: textSpanClasses,
          style: buttonTextAdjust,
        },
        {},
        buttonText,
      );

      videojs.dom.appendContent(el, textSpan);

      // BUTTON MEMORY
      // Check if Button Variable Memory is set and if Button History Memory is set
      let buttonVariableMemory;
      let buttonVariableMemoryValue;
      let buttonVariableMemoryName;
      let buttonHistoryMemory = choice.button_history_memory || false;
      let linkedVariantHasBeenSeen = false;
      buttonVariableMemory = choice.button_variable_memory || null;

      if (buttonVariableMemory && choice) {
        buttonVariableMemoryName = choice.button_variable_memory?.toLowerCase()?.replace(/\s/g, '');
        buttonVariableMemoryValue = String(this.aLContext.currentContext[buttonVariableMemoryName]).toLowerCase().replace(/\s/g, '') === 'true';

        el.setAttribute('data-memory-variable', buttonVariableMemoryName);
      }

      if (buttonHistoryMemory) {
        let linkedVariantId;
        if (choice.conditional_logic_block_present) {
          // Game logic check - disabled for testing
          linkedVariantId = getALLinkedVariantId({ choice, aLContext });
        } else {
          // The id of the next variant. It will play a direct choice or the result of a conditional choice.

          linkedVariantId = getLinkedVariantId({ choice, history });
        }

        linkedVariantHasBeenSeen = isVariantInHistory(linkedVariantId, history);
      }

      // Check if the choice has a buttonVariableMemory or buttonHistoryMemory and is true in the context
      if ((buttonVariableMemory && buttonVariableMemoryValue) || (buttonHistoryMemory && linkedVariantHasBeenSeen)) {
        // If either memory type is true and not already set to toggle--active
        if (!el.classList.contains('toggle--active')) {
          el.classList.remove('toggle--inactive');
          el.classList.add('toggle--active');
        }

        // If either memory type is true and the choice has an active image, show it
        if (choice.image_active_url) {
          el.childNodes[1].classList.remove('js-image-hide');

          // and if the choice has a normal image, hide it
          if (choice.image_url) {
            el.childNodes[0].classList.add('js-image-hide');
          }

          // and if the choice has a hover image, add the memory/toggle hover class for active image
          if (choice.image_hover_url) {
            el.childNodes[1].classList.add('js-toggle-active-hover-hide');
          }
        } else {
          // Invert background color and text color
          const { background_color, text_color } = variant.prompt;

          if (!isTransparent(background_color) && !isTransparent(text_color)) {
            el.style.background = text_color;
            el.querySelector('.vjs-wrp-button__text').style.color = background_color;
          }
          // if (!isTransparent(background_color) && !isTransparent(text_color)) {
          //   el.style.background = text_color;
          //   console.log('Set background for memory to: ', text_color);
          //   el.style.fontWeight = '900';
          //   el.querySelector('.vjs-wrp-button__text').style.color = background_color;
          // }
        }
      } else if (buttonVariableMemory && !buttonVariableMemoryValue && !(buttonHistoryMemory && linkedVariantHasBeenSeen)) {
        // If the buttonVariableMemory or buttonHistoryMemory is false
        // and not already set to toggle--inactive
        if (!el.classList.contains('toggle--inactive')) {
          el.classList.remove('toggle--active');
          el.classList.add('toggle--inactive');
        }

        // If the buttonVariableMemory is false and the choice has a normal image, show it
        if (choice.image_url) {
          el.childNodes[0].classList.remove('js-image-hide');

          // and if the choice has an active image, hide it
          if (choice.image_active_url) el.childNodes[1].classList.add('js-image-hide');
        } else {
          // Use regular background color and text color
          let { background_color, text_color } = variant.prompt;
          if (!isTransparent(background_color) && !isTransparent(text_color)) {
            el.style.backgroundColor = background_color;
            el.querySelector('.vjs-wrp-button__text').style.color = text_color;
          }
        }
      }

      const formattedChoice = {
        el: el,
        callback: (event) => {
          // If the button has the .js-choice-unlinked class - ie will be toggled when clicked
          if (event.currentTarget.classList.contains('js-choice-unlinked')) {
            // Toggle between .toggle--active and .toggle--inactive for css and selector purposes when toggle clicked
            event.currentTarget.classList.contains('toggle--active')
              ? (event.currentTarget.classList.remove('toggle--active'), event.currentTarget.classList.add('toggle--inactive'))
              : (event.currentTarget.classList.remove('toggle--inactive'), event.currentTarget.classList.add('toggle--active'));

            // If the choice has images
            if (choice.image_url && choice.image_active_url) {
              // Then toggle normal and active image each time it's toggle clicked
              event.currentTarget.childNodes[0].classList.toggle('js-image-hide');
              event.currentTarget.childNodes[1].classList.toggle('js-image-hide');
              event.currentTarget.childNodes[1].classList.remove('js-toggle-active-hover-hide');
            } else {
              // Otherwise, invert background color and text color each time it's toggle clicked
              const currentBackgroundColor = event.currentTarget.style.background;
              const currentTextColor = event.currentTarget.querySelector('.vjs-wrp-button__text').style.color;
              if (currentBackgroundColor && currentTextColor && !isTransparent(currentBackgroundColor) && !isTransparent(currentTextColor)) {
                event.currentTarget.style.background = currentTextColor;
                event.currentTarget.querySelector('.vjs-wrp-button__text').style.color = currentBackgroundColor;
              }
            }

            const unlinkedChoiceMadeEvent = new CustomEvent(EVENT_UNLINKED_CHOICE, {
              bubbles: true,
              detail: {
                unlinkedChoice: choice,
                goToTime: choice.go_to_time,
              },
            });
            event.currentTarget.dispatchEvent(unlinkedChoiceMadeEvent);

            const buttons = document.querySelectorAll('.vjs-wrp-button[data-memory-variable]');

            buttons.forEach((button) => {
              const memoryVariable = button.getAttribute('data-memory-variable');

              if (memoryVariable) {
                let newValue = this.aLContext.currentContext[memoryVariable] === 'true';

                if (newValue) {
                  button.classList.add('toggle--active');
                  button.classList.remove('toggle--inactive');
                } else {
                  button.classList.remove('toggle--active');
                  button.classList.add('toggle--inactive');
                }

                // If button has base and active images, swap images
                const baseImage = button.querySelector('.js-image-base');
                const activeImage = button.querySelector('.js-image-active');

                if (baseImage && activeImage) {
                  if (newValue) {
                    // Show active image, hide base image
                    baseImage.classList.add('js-image-hide');
                    activeImage.classList.remove('js-image-hide');
                    activeImage.classList.remove('js-toggle-active-hover-hide'); // Ensure hover stays correct
                  } else {
                    // Show base image, hide active image
                    baseImage.classList.remove('js-image-hide');
                    activeImage.classList.add('js-image-hide');
                  }
                } else if (newValue) {
                  let { background_color, text_color } = variant.prompt;
                  if (!isTransparent(background_color) && !isTransparent(text_color)) {
                    button.style.background = text_color;
                    button.querySelector('.vjs-wrp-button__text').style.color = background_color;
                  }
                } else {
                  let { background_color, text_color } = variant.prompt;
                  if (!isTransparent(background_color) && !isTransparent(text_color)) {
                    button.style.background = background_color;
                    button.querySelector('.vjs-wrp-button__text').style.color = text_color;
                  }
                }
              }
            });

            // Stop further execution
            return;
          }
          // Pause the player on link clicks
          if (event.currentTarget.hasAttribute('href')) {
            this.player.pause();

            const href = event.currentTarget.getAttribute('href');
            if (href.startsWith('https://studio.stornaway.io/templates')) {
              if (event.currentTarget.classList.contains('external-link-disabled')) {
                event.preventDefault();
              } else {
                event.currentTarget.classList.add('external-link-disabled');
              }
            }
            return;
          } else if (this.player.paused() && !this.player.ended() && this.player.isVideo) {
            this.player.play();
          }

          // Prevents choice from pausing the video.
          event.stopPropagation();

          // Used to prevent a choice from being re-selected.
          const choiceHasBeenMade = this.container.querySelectorAll('.vjs-wrp-button[aria-pressed="true"]').length;

          if (!choiceHasBeenMade) {
            // Dispatched event to set select a choice.
            // Exposes selected to be assigned to the respective player.
            const choiceMadeEvent = new CustomEvent(EVENT_WILL_SELECT_CHOICE, {
              bubbles: true,
              detail: {
                selectedChoice: choice,
                goToTime: choice.go_to_time,
              },
            });
            event.currentTarget.dispatchEvent(choiceMadeEvent);

            // Exposes selected to be assigned to the respective player.
            this.selectedChoice = choice;

            // Adds checkmark to selected choice.
            event.currentTarget.setAttribute('aria-pressed', true);

            // All buttons but the one selected.
            const buttons = this.container.querySelectorAll('.vjs-wrp-button');

            // Disables buttons once a choice has been made.
            // However, in the future it could be used to reset a choice instead if the user changes it during the current variant.
            buttons.forEach((button) => {
              if (!button.classList.contains('js-choice-unlinked')) {
                button.setAttribute('disabled', 'true');
              } else {
                // If the button is unlinked and has the class 'toggle--active', remove it and add 'toggle--inactive'
                // Otherwise, remove 'toggle--inactive' and add 'toggle--active'
                button.classList.contains('toggle--active')
                  ? (button.classList.remove('toggle--active'), button.classList.add('toggle--inactive'))
                  : (button.classList.remove('toggle--inactive'), button.classList.add('toggle--active'));
              }
            });

            // Inverts styles for selected option.
            const checked = this.el().querySelector(`.vjs-wrp-button[aria-pressed="true"]`);
            if (checked) {
              if (choice.image_active_url) {
                // Run image click logic if there's an active image
                const baseImage = checked.querySelector('.js-image-base'),
                  activeImage = checked.querySelector('.js-image-active'),
                  hoverImage = checked.querySelector('.js-image-hover');
                if (activeImage) {
                  activeImage.classList.remove('js-image-hide'); // Show active image if it exists
                  activeImage.classList.remove('js-toggle-active-hover-hide'); // Hide active during hover unless no hover
                }
                if (baseImage) baseImage.classList.add('js-image-hide'); // Hide base image if it exists
                if (hoverImage) {
                  hoverImage.classList.add('js-image-hide'); // Hide hover image if it exists
                  activeImage.classList.remove('has-hover'); // Show active during hover if there's hover
                }
              } else {
                const { background_color, text_color } = variant.prompt;
                if (!isTransparent(background_color) && !isTransparent(text_color)) {
                  checked.style.backgroundColor = text_color;
                  checked.querySelector('.vjs-wrp-button__text').style.color = background_color;
                }

                checked.style.fontWeight = 'bold';
              }
            }
          }
        },
      };
      result.push(formattedChoice);
      return result;
    }, []);

    choicesElements.forEach((choiceElement) => {
      this.container.appendChild(choiceElement.el);
      choiceElement.el.addEventListener('click', choiceElement.callback);
    });

    // otherwise clicking on this element would pause the video
    // Commented out (Jonny) due to stopping cut on click when text is clicked

    // $('.vjs-wrp-button__text').click(event => {
    //   event.stopPropagation();
    // });
  }
}
